import { useCallback, useMemo, useState } from "react";

import type { AvailabilityMode, AvailabilityState, DayTimes } from "./types";
import type { AvailabilityWindow } from "./schemas/availability";
import { DEFAULT_RANGE } from "./constants";
import { dayValid, rangeValid } from "./validation";

export type TimeTarget = "shared" | string;

function freshDay(): DayTimes {
  return { anyTime: false, ranges: [{ ...DEFAULT_RANGE }] };
}

const INITIAL: AvailabilityState = {
  dates: [],
  mode: "SHARED",
  shared: freshDay(),
  perDay: {},
};

/**
 * Convert a flat list of {@link AvailabilityWindow}s (the wire shape) back
 * into the per-day {@link AvailabilityState} the picker uses internally.
 * Always seeds {@code mode: "PER_DAY"} since multiple windows on different
 * dates may have different times — there's no safe way to collapse to SHARED.
 */
export function fromWindows(
  windows: ReadonlyArray<AvailabilityWindow>,
): Partial<AvailabilityState> {
  if (!windows.length) return {};
  const dates: Array<string> = [];
  const perDay: Record<string, DayTimes> = {};
  for (const w of windows) {
    if (!dates.includes(w.date)) {
      dates.push(w.date);
      perDay[w.date] = { anyTime: false, ranges: [] };
    }
    const day = perDay[w.date];
    if (w.anyTime) {
      day.anyTime = true;
      day.ranges = [];
    } else if (
      !day.anyTime &&
      w.startMinute != null &&
      w.endMinute != null
    ) {
      day.ranges.push({
        startMinute: w.startMinute,
        endMinute: w.endMinute,
      });
    }
  }
  return { dates: dates.sort(), perDay, mode: "PER_DAY" };
}

export function useAvailability(initial?: Partial<AvailabilityState>) {
  const [state, setState] = useState<AvailabilityState>({
    ...INITIAL,
    ...initial,
  });

  const toggleDate = useCallback((date: string) => {
    setState((s) => {
      const has = s.dates.includes(date);
      const dates = has
        ? s.dates.filter((d) => d !== date)
        : [...s.dates, date].sort();
      const perDay = { ...s.perDay };
      if (has) delete perDay[date];
      else perDay[date] = freshDay();
      return { ...s, dates, perDay };
    });
  }, []);

  const setMode = useCallback((mode: AvailabilityMode) => {
    setState((s) => {
      const perDay = { ...s.perDay };
      if (mode === "PER_DAY") {
        for (const d of s.dates) {
          if (!(d in perDay)) {
            perDay[d] = {
              anyTime: s.shared.anyTime,
              ranges: s.shared.ranges.map((r) => ({ ...r })),
            };
          }
        }
      }
      return { ...s, mode, perDay };
    });
  }, []);

  const editDay = useCallback(
    (target: TimeTarget, fn: (t: DayTimes) => DayTimes) => {
      setState((s) => {
        if (target === "shared") return { ...s, shared: fn(s.shared) };
        const current = s.perDay[target] ?? freshDay();
        return { ...s, perDay: { ...s.perDay, [target]: fn(current) } };
      });
    },
    [],
  );

  const setAnyTime = useCallback(
    (target: TimeTarget, anyTime: boolean) =>
      editDay(target, (t) => ({ ...t, anyTime })),
    [editDay],
  );
  const addRange = useCallback(
    (target: TimeTarget) =>
      editDay(target, (t) => ({
        ...t,
        ranges: [...t.ranges, { ...DEFAULT_RANGE }],
      })),
    [editDay],
  );
  const removeRange = useCallback(
    (target: TimeTarget, index: number) =>
      editDay(target, (t) => ({
        ...t,
        ranges: t.ranges.filter((_, i) => i !== index),
      })),
    [editDay],
  );
  const updateRange = useCallback(
    (
      target: TimeTarget,
      index: number,
      patch: Partial<{ startMinute: number; endMinute: number }>,
    ) =>
      editDay(target, (t) => ({
        ...t,
        ranges: t.ranges.map((r, i) => (i === index ? { ...r, ...patch } : r)),
      })),
    [editDay],
  );

  const reset = useCallback(() => setState(INITIAL), []);

  const toWindows = useCallback((): Array<AvailabilityWindow> => {
    const out: Array<AvailabilityWindow> = [];
    for (const date of [...state.dates].sort()) {
      const t =
        state.mode === "SHARED"
          ? state.shared
          : (state.perDay[date] ?? freshDay());
      if (t.anyTime) {
        out.push({ date, anyTime: true });
      } else {
        for (const r of t.ranges) {
          if (rangeValid(r))
            out.push({
              date,
              anyTime: false,
              startMinute: r.startMinute,
              endMinute: r.endMinute,
            });
        }
      }
    }
    return out;
  }, [state]);

  const isValid = useMemo(() => {
    if (state.dates.length === 0) return false;
    if (state.mode === "SHARED") return dayValid(state.shared);
    return state.dates.every((d) => dayValid(state.perDay[d] ?? freshDay()));
  }, [state]);

  return useMemo(
    () => ({
      state,
      toggleDate,
      setMode,
      setAnyTime,
      addRange,
      removeRange,
      updateRange,
      reset,
      toWindows,
      isValid,
    }),
    [
      state,
      toggleDate,
      setMode,
      setAnyTime,
      addRange,
      removeRange,
      updateRange,
      reset,
      toWindows,
      isValid,
    ],
  );
}

export type AvailabilityController = ReturnType<typeof useAvailability>;
